import React, {useState} from 'react';
import ButtonSolid from 'components/Buttons/ButtonSolid';
import {RiseInterstitialProps} from 'screens/InsomniaSurvey/components/RiseInterstitialTemplate/RiseInterstitialBase';
import styles from './InsuranceNotCoveredInterstitial.module.css';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import Analytics from 'utils/Analytics';
import {MEDICARE_WAITLIST_COPY} from 'surveys/insomnia_funnel/survey/config';

const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

/**
 * `capacity` — the clinic is paused for everyone (the `/start` SleepioRx funnel).
 * `coverage` — the clinic is booking, but not for this state/payer yet
 * (the `/book-appointment` funnel).
 */
type NotCoveredVariant = 'capacity' | 'coverage';

const COPY: Record<NotCoveredVariant, {pill: string; headline: string; body: string}> = {
  capacity: {
    pill: 'Clinic availability update',
    headline: "We're not currently accepting new patients with your insurance in your state",
    body: "We're at capacity for the clinic right now. Join the waitlist and we'll reach out if appointment availability opens up.",
  },
  coverage: {
    pill: 'Coverage update',
    headline: "We're not currently accepting your insurance in your state",
    body: "We're actively working to expand coverage to more states and insurance providers. Join the waitlist and we'll reach out as soon as we can see you.",
  },
};

type InsuranceNotCoveredInterstitialProps = RiseInterstitialProps & {
  variant?: NotCoveredVariant;
};

const InsuranceNotCoveredInterstitial: React.FC<InsuranceNotCoveredInterstitialProps> = ({variant = 'capacity'}) => {
  const isMedicare = surveyInstance.getValue('insurance_coverage') === 'medicare';
  const copy = isMedicare ? {...COPY[variant], ...MEDICARE_WAITLIST_COPY} : COPY[variant];
  const savedEmail = surveyInstance.getValue('clinic_waitlist_email') ?? surveyInstance.getValue('email') ?? '';
  const [email, setEmail] = useState(savedEmail);
  const [emailError, setEmailError] = useState('');

  const handleSubmit = () => {
    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }

    surveyInstance.setValue('clinic_waitlist_email', trimmedEmail);
    surveyInstance.setValue('email', trimmedEmail);
    SurveyData.save(surveyInstance);
    Analytics.identify({
      email: trimmedEmail,
      clinic_waitlist_joined: 'true',
      ...(isMedicare ? {medicare_waitlist_joined: 'true'} : {}),
    });
    Analytics.track('Clinic Web Intake Waitlist Submitted', {
      source_screen: variant === 'coverage' ? 'clinic_coverage_waitlist' : 'clinic_capacity_waitlist',
      has_email: true,
      insurance_state: surveyInstance.getValue('insurance_state'),
      insurance_coverage: surveyInstance.getValue('insurance_coverage'),
      clinic_booking_paused: surveyInstance.getValue('clinic_booking_paused'),
      medicare_plan_type: isMedicare ? surveyInstance.getValue('medicare_plan_type') : undefined,
    });

    const appHandoffPage = surveyInstance.getPageByName('page-rise_app_handoff_interstitial');
    if (appHandoffPage) {
      surveyInstance.currentPage = appHandoffPage;
      SurveyData.save(surveyInstance);
      return;
    }

    surveyInstance.nextPage();
  };

  return (
    <div className={styles.container}>
      <div className={styles.statusPill}>{copy.pill}</div>
      <h1 className={styles.headerText}>{copy.headline}</h1>
      <div className={styles.bodyText}>
        <p>{copy.body}</p>
      </div>

      <div className={styles.waitlistForm}>
        <div className={styles.formHeader}>
          <div className={styles.formIcon}>@</div>
          <div>
            <div className={styles.formTitle}>Join the waitlist</div>
            <div className={styles.formSubtitle}>We'll only use this for clinic availability updates.</div>
          </div>
        </div>
        <label className={styles.inputLabel} htmlFor="clinic-waitlist-email">
          Email address
        </label>
        <input
          id="clinic-waitlist-email"
          className={`${styles.emailInput} ${emailError ? styles.emailInputError : ''}`}
          value={email}
          onChange={event => {
            setEmail(event.target.value);
            setEmailError('');
          }}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleSubmit();
            }
          }}
          aria-invalid={Boolean(emailError)}
          aria-describedby={emailError ? 'clinic-waitlist-email-error' : undefined}
        />
        {emailError ? (
          <div id="clinic-waitlist-email-error" className={styles.errorText}>
            {emailError}
          </div>
        ) : null}
        <ButtonSolid title="Continue" variant="purple" onClick={handleSubmit} />
      </div>
    </div>
  );
};

export default InsuranceNotCoveredInterstitial;
